import { Injectable } from '@angular/core';
import { combineLatest, finalize, of } from 'rxjs';
import { concatMap } from 'rxjs/operators';

import { ProgramTemplatesBackend } from '@libs/src/backend/program-templates.backend';
import { PlatformConfigBackend } from '@libs/src/platform-config/platform-config.backend';

import { areTemplatesTheSame } from './platform-config-hub-sync.helper';
import { Group } from '@libs/src/models/group.model';
import { ProgramTemplateStatuses } from '@libs/src/models/program-template.model';
import { programTemplateFormFields } from '@main-client/src/app/create-development-program/create-development-program-template.helper';

import { chunkOperations, nonEmptyValue$ } from '../rxjs/rxjs.helper';

import pick from 'lodash-es/pick';

@Injectable({ providedIn: 'root' })
export class PlatformConfigHubSyncService {
  private isSyncing = false;
  private hasSynced = false;

  constructor(
    private readonly platformConfigBackend: PlatformConfigBackend,
    private readonly programTemplatesService: ProgramTemplatesBackend,
  ) {}

  syncPlatformTemplatesWithHub(
    group: Pick<Group, '_id' | 'development_programs_enabled'>,
  ) {
    if (
      !group.development_programs_enabled ||
      this.isSyncing ||
      this.hasSynced
    ) {
      return nonEmptyValue$;
    }
    this.isSyncing = true;
    return combineLatest([
      this.programTemplatesService.v2.listProgramTemplates(group._id, {
        limit: '100',
        skip: '0',
      }),
      this.platformConfigBackend.getProgramTemplates(),
    ]).pipe(
      concatMap(
        ([
          { programTemplates: hubTemplates },
          { programTemplates: platformTemplates },
        ]) => {
          const hubTemplatesWithPlatformConfig = hubTemplates.filter(
            ({ platform_config }) => platform_config,
          );
          const platformTemplateIds = new Set(
            platformTemplates
              .filter(({ auto_create_in_hub }) => auto_create_in_hub)
              .map(({ _id }) => _id),
          );
          const hubTemplatesToRemove = hubTemplatesWithPlatformConfig.filter(
            ({ platform_config }) => !platformTemplateIds.has(platform_config),
          );
          const hubTemplatesToUpdate = hubTemplatesWithPlatformConfig.filter(
            (hubTemplate) => {
              if (!platformTemplateIds.has(hubTemplate.platform_config)) {
                return false;
              }
              const platformTemplate = platformTemplates.find(
                ({ _id: platformConfigId }) =>
                  platformConfigId === hubTemplate.platform_config,
              );
              if (!platformTemplate) {
                return false;
              }
              return !areTemplatesTheSame(
                platformTemplate.program_template,
                hubTemplate,
              );
            },
          );
          const platformTemplatesToAdd = platformTemplates.filter(
            ({ _id, auto_create_in_hub }) =>
              auto_create_in_hub &&
              !hubTemplatesWithPlatformConfig.find(
                ({ platform_config }) => platform_config === _id,
              ),
          );
          const operations = [
            ...hubTemplatesToRemove.map((template) =>
              this.programTemplatesService.v2.patchProgramTemplate(
                group._id,
                template._id,
                { status: ProgramTemplateStatuses.deleted },
              ),
            ),
            ...hubTemplatesToUpdate.map((template) => {
              const platformTemplate = platformTemplates.find(
                ({ _id: platformConfigId }) =>
                  platformConfigId === template.platform_config,
              );
              return this.programTemplatesService.v2.patchProgramTemplate(
                group._id,
                template._id,
                {
                  ...pick(
                    platformTemplate.program_template,
                    programTemplateFormFields,
                  ),
                },
              );
            }),
            ...platformTemplatesToAdd.map((template) =>
              this.programTemplatesService.v2.createProgramTemplate(group._id, {
                ...template.program_template,
                platform_config: template._id,
              }),
            ),
          ];
          return operations.length
            ? chunkOperations(operations)
            : nonEmptyValue$;
        },
      ),
      finalize(() => {
        this.isSyncing = false;
        this.hasSynced = true;
      }),
    );
  }
}
